/* ==========================================================================
   Katalyst Foundation — project script
   Boundary: never edit shipped Avante files. Uses Avante's documented events;
   no polling, no MutationObserver. Every behaviour pauses when hidden and
   respects prefers-reduced-motion.
   Spec: .plans/09-mods-ledger.md
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     MOD-038 / KF-103 — Story slide captions.
     Tapping a pathway circle opens Avante's story viewer. The design sets a caption on every
     slide: a small uppercase eyebrow naming the programme ("TERTIARY SUPPORT PROGRAM") over a
     large serif headline, the pathway's tagline ("Transforming lives through learning."),
     sitting just above the Give Now button.

     The `story` block has no text setting of any kind (image_N / link_N / label_N / products_N
     only — stories-slideshow.liquid schema), and stories-slide.liquid is shipped, so the copy
     lives in the `custom-story-captions` section and is injected here. That section emits a
     hidden `.kf-story-desc-src` holding one `[data-for]` group per story, each with a
     `[data-headline]` and up to eight `[data-eyebrow="N"]` spans, N matching the story's
     Image N.

     Nothing is injected under the circles. The approved design shows the pathway title alone
     there; an earlier pass added the tagline beneath each circle, which the design never had.

     Stories are matched to their copy through the thumbnail title, because the viewer's own
     user-name element carries the title twice (a rolling-hover copy, stories-slideshow.liquid
     :351-353). Slides are matched by position: Avante renders only the images that are set, and
     both the outer and inner Swipers run with `loop: false` (global.js:5938, :6000), so there
     are no cloned slides to double up on.
     ------------------------------------------------------------------------ */
  function injectStoryCaptions() {
    var src = document.querySelector('.kf-story-desc-src');
    if (!src) return;

    var copy = {};
    src.querySelectorAll('[data-for]').forEach(function (group) {
      var headline = group.querySelector('[data-headline]');
      var eyebrows = {};
      group.querySelectorAll('[data-eyebrow]').forEach(function (el) {
        eyebrows[el.getAttribute('data-eyebrow')] = el.textContent.trim();
      });
      copy[group.getAttribute('data-for').trim().toLowerCase()] = {
        headline: headline ? headline.textContent.trim() : '',
        eyebrows: eyebrows
      };
    });
    if (!Object.keys(copy).length) return;

    document.querySelectorAll('.stories-slideshow .stories-slides[data-slide-id]').forEach(function (story) {
      var id = story.getAttribute('data-slide-id');
      var root = story.closest('.stories-slideshow');
      var titleEl = root && root.querySelector(
        '.stories-slideshow__thumbnail[data-thumbnail-id="' + id + '"] .stories-slideshow__title'
      );
      if (!titleEl) return;
      var entry = copy[titleEl.textContent.trim().toLowerCase()];
      if (!entry) return;

      var slides = story.querySelectorAll('.swiper-story-inner > .swiper-wrapper > .swiper-slide');
      Array.prototype.forEach.call(slides, function (slide, i) {
        var content = slide.querySelector('.stories-slider-content');
        if (!content || content.querySelector('.kf-slide-caption')) return;
        var eyebrow = entry.eyebrows[String(i + 1)] || '';
        if (!eyebrow && !entry.headline) return;

        var cap = document.createElement('div');
        cap.className = 'kf-slide-caption';
        if (eyebrow) {
          var e = document.createElement('p');
          e.className = 'kf-slide-caption__eyebrow';
          e.textContent = eyebrow;
          cap.appendChild(e);
        }
        if (entry.headline) {
          var h = document.createElement('p');
          h.className = 'kf-slide-caption__headline';
          h.textContent = entry.headline;
          cap.appendChild(h);
        }
        content.classList.add('kf-slide-has-caption');
        content.appendChild(cap);
      });
    });
  }

  /* ------------------------------------------------------------------------
     MOD-007 — Rail position counter ("01 / 07").
     `banners` exposes no counter setting. Reads the rail's own slides and
     tracks the most-visible one with IntersectionObserver rather than polling.
     Documented fallback if the rail markup changes: arrows only, no counter.
     .plans/02-homepage.md band 6
     ------------------------------------------------------------------------ */
  function initRail(rail) {
    var track = rail.querySelector('[data-kf-rail-track]');
    if (!track) return;
    var items = track.querySelectorAll('[data-kf-rail-item]');
    if (items.length < 2) return;

    var prev = rail.querySelector('[data-kf-rail-prev]');
    var next = rail.querySelector('[data-kf-rail-next]');
    var counter = rail.querySelector('[data-kf-rail-counter]');
    var progress = rail.querySelector('[data-kf-rail-progress]');
    var pad = function (n) { return String(n).padStart(2, '0'); };
    var total = items.length;
    var current = 1;

    // KF-102 — slide progress. Thumb width = visible share of the rail; position = scroll
    // progress. Written as two custom properties so the CSS owns the drawing.
    function paintProgress() {
      if (!progress) return;
      var range = track.scrollWidth - track.clientWidth;
      var share = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
      var at = range > 0 ? Math.min(1, Math.max(0, track.scrollLeft / range)) : 0;
      progress.style.setProperty('--kf-rail-thumb', share.toFixed(4));
      progress.style.setProperty('--kf-rail-at', at.toFixed(4));
    }

    function paint() {
      paintProgress();
      if (counter) counter.textContent = pad(current) + ' / ' + pad(total);
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) {
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      }
    }

    function step(dir) {
      var first = items[0];
      var gap = parseFloat(getComputedStyle(track).columnGap || '24') || 24;
      var by = (first ? first.getBoundingClientRect().width : track.clientWidth * 0.8) + gap;
      track.scrollBy({
        left: dir * by,
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });

    // track the most-visible card rather than polling scroll position
    var ratios = new Array(total).fill(0);
    var io = new IntersectionObserver(function (entries) {
      if (document.hidden) return;
      entries.forEach(function (e) {
        var i = Array.prototype.indexOf.call(items, e.target);
        if (i > -1) ratios[i] = e.intersectionRatio;
      });
      var best = ratios.indexOf(Math.max.apply(null, ratios));
      if (best > -1) current = best + 1;
      paint();
    }, { root: track, threshold: [0, 0.25, 0.5, 0.75, 1] });

    items.forEach(function (el) { io.observe(el); });
    track.addEventListener('scroll', paint, { passive: true });
    // The visible share changes with the viewport (cards per view), not only with scroll.
    if (progress && typeof window.ResizeObserver === 'function') {
      new ResizeObserver(paintProgress).observe(track);
    }
    paint();
  }

  function initRailCounters() {
    document.querySelectorAll('.kf-rail').forEach(initRail);
  }

  /* ------------------------------------------------------------------------
     MOD-012 / MOD-044 — programme-row parallax: REMOVED, superseded by MOD-051.

     This was a scroll listener writing translate3d onto .kf-prog__media from rAF. It was
     reported not working five times and rebuilt three; each rebuild moved the image further
     (48 -> 128 -> 256 -> ~470px) and none of them looked like the demo, because the demo does
     not translate the image at all. It pins it: position fixed, height 100dvh, no transform,
     no animation. Measured on the live band, not inferred. MOD-051 in custom-styles.css.

     Deleted rather than kept as a fallback: a transform on the now-fixed media would create a
     containing block for it and break the pinning outright, so leaving this in would actively
     undo MOD-051 on every browser that still ran it.
     ------------------------------------------------------------------------ */

  /* ------------------------------------------------------------------------
     MOD-048 — warm GSAP before the first hover on a link-list band.

     Avante's HoverImageReveal (assets/global.js:9557) reveals the image through
     TweenMax, which it loads lazily on the FIRST mouseenter:

         if (window.TweenMax && window.TimelineMax) { this.showImage(); return; }
         TweenMaxLoader.load().then(...)

     On a cold page TweenMax is not there yet, so the first hover takes the async
     branch and the load does not resolve before the pointer has moved on — the
     reveal is simply lost. Measured on Our Work at 1440:

         1st hover (Educate)   0 | 0 | 0     <- nothing
         2nd hover (Heal)      0 | 1 | 0
         3rd hover (Build)     0 | 0 | 1
         4th hover (Educate)   1 | 0 | 0

     Every hover after the first is correct, which is the worst shape this bug
     could take: the person who hovers once concludes it is broken, and the
     person who hovers twice never sees the problem.

     Fix is to warm the library BEFORE the pointer reaches a word, not to load it
     eagerly — a page with no link-list should not pay for GSAP at all. The first
     pointermove or scroll anywhere on the document happens long before anyone
     deliberately hovers a word, so that is the trigger.

     `TweenMaxLoader` itself is a script-scoped const in global.js and cannot be
     reached, but the URL it uses is published on window.themeAssets, and the
     loader checks for an existing `[data-tweenmax]` script and reuses it. So this
     cooperates with the shipped loader rather than racing it.
     ------------------------------------------------------------------------ */
  function warmHoverImages() {
    if (!document.querySelector('link-hover-image')) return;

    var src = window.themeAssets && window.themeAssets.tweenMaxSrc;
    if (!src) return;
    if (window.TweenMax || document.querySelector('script[data-tweenmax="1"]')) return;

    var s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.setAttribute('data-tweenmax', '1');   // the shipped loader looks for exactly this
    document.head.appendChild(s);
  }

  function armWarm() {
    if (!document.querySelector('link-hover-image')) return;   // nothing to warm for

    var done = false;
    function once() {
      if (done) return;
      done = true;
      window.removeEventListener('pointermove', once);
      window.removeEventListener('scroll', once);
      try { warmHoverImages(); } catch (e) { /* progressive enhancement */ }
    }

    // Idle is the primary trigger, not the interaction events. Waiting for the first
    // pointermove sounds early enough and is not: on a trackpad the first move can BE the
    // move onto a word, leaving no time to fetch ~50KB before mouseenter fires. Measured —
    // warming on pointermove alone still lost the first hover. Idle runs while the page is
    // doing nothing, which is exactly when this should happen.
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(once, { timeout: 2000 });
    } else {
      setTimeout(once, 600);
    }

    // Interaction still arms it as a floor, for browsers that never go idle.
    window.addEventListener('pointermove', once, { passive: true, once: true });
    window.addEventListener('scroll', once, { passive: true, once: true });
  }

  /* ------------------------------------------------------------------------
     MOD-064 — GiveNow embed (sections/custom-donation-card.liquid).

     The provider ships a parser-blocking <script src> followed by an inline
     iFrameResize() call. Neither belongs in a section body: the blocking tag
     costs every visitor a third-party round trip whether or not they scroll to
     the form, and async/defer would make the inline call race the library.
     So the section renders only the frame, marked data-kf-givenow="pending",
     and the resizer is fetched once per page the first time a frame nears the
     viewport. IntersectionObserver, one-shot — not polling, not a
     MutationObserver.

     The vendor snippet hardcodes id="cause12256"; the section emits a unique
     id per section instance so two embeds on one page both resize.
     ------------------------------------------------------------------------ */
  var GIVENOW_RESIZER = 'https://www.givenow.com.au/js/iframe-resizer/iframeResizer.min.js?version=4.3.2.1';

  function resizeGiveNow(frame) {
    if (typeof window.iFrameResize !== 'function') return;
    if (frame.getAttribute('data-kf-givenow') === 'ready') return;
    frame.setAttribute('data-kf-givenow', 'ready');

    window.iFrameResize({
      checkOrigin: false,
      // GiveNow asks the parent to scroll when it moves between steps. Returning
      // false tells the library we handled it, so it does not scroll as well.
      onScroll: function () {
        var bonus = parseFloat(frame.getAttribute('data-offset-bonus')) || 0;
        var offset = frame.getBoundingClientRect().top - document.body.getBoundingClientRect().top;
        window.scrollTo({
          top: offset - bonus,
          behavior: reduceMotion.matches ? 'auto' : 'smooth'
        });
        return false;
      }
    }, '#' + frame.id);
  }

  function loadGiveNow(frame) {
    frame.setAttribute('data-kf-givenow', 'loading');

    if (typeof window.iFrameResize === 'function') {
      resizeGiveNow(frame);
      return;
    }

    var tag = document.querySelector('script[data-kf-givenow-resizer]');
    if (tag && tag.getAttribute('data-loaded') === '1') {
      resizeGiveNow(frame);
      return;
    }

    if (!tag) {
      tag = document.createElement('script');
      tag.src = frame.getAttribute('data-kf-givenow-resizer') || GIVENOW_RESIZER;
      tag.async = true;
      tag.setAttribute('data-kf-givenow-resizer', '1');
      document.head.appendChild(tag);
    }

    // One listener per frame; the second embed on a page reuses the same tag.
    tag.addEventListener('load', function () {
      tag.setAttribute('data-loaded', '1');
      resizeGiveNow(frame);
    });
    tag.addEventListener('error', function () {
      // Leave the frame at its configured height — the form still works, it
      // just cannot tell us how tall it wants to be.
      frame.setAttribute('data-kf-givenow', 'failed');
    });
  }

  function initGiveNow() {
    // Only "pending" frames: a re-init after a section reload must not observe
    // or re-resize a frame that is already loading or ready.
    var frames = document.querySelectorAll('iframe[data-kf-givenow="pending"]');
    if (!frames.length) return;

    if (typeof window.IntersectionObserver !== 'function') {
      frames.forEach(loadGiveNow);
      return;
    }

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        obs.unobserve(entry.target);
        loadGiveNow(entry.target);
      });
    }, { rootMargin: '400px' });

    frames.forEach(function (frame) { io.observe(frame); });
  }

  /* ------------------------------------------------------------------------
     Homepage "Our impact in numbers" band on phones: scroll-driven horizontal travel.
     Avante's BannerHScroll pins the band and moves it sideways as the page scrolls on desktop,
     but disables itself below 769px (global.js:11377) and leaves a swipe. The design wants the
     strip to move with the page on phones too.

     NOT pinned. A pin needs a scroll runway as tall as the strip's overflow (~1600px here), and
     on a phone the 520px band is shorter than the screen, so that runway showed as a huge empty
     cream area below the band. Instead the strip travels across its full length while the band
     crosses the viewport — first panel as it enters from the bottom, last panel as it leaves at
     the top — with no added height. Scroll events only (rAF-coalesced, passive), no timers, and
     nothing under prefers-reduced-motion, where the native swipe stays.
     CSS side: custom-styles.css "[data-kf-hscroll]".
     ------------------------------------------------------------------------ */
  var phoneHScroll = window.matchMedia('(max-width: 768px)');

  function initMobileHScroll() {
    document.querySelectorAll('.horizontal-banners').forEach(function (root) {
      if (root.__kfHScroll) { root.__kfHScroll.update(); return; }

      var wrapper = root.querySelector('.horizontal-banners__wrapper');
      var track = root.querySelector('.horizontal-banners__blocks');
      if (!wrapper || !track) return;

      var maxX = 0;
      var frame = 0;

      function paint() {
        frame = 0;
        if (!root.hasAttribute('data-kf-hscroll') || maxX <= 0) return;
        // 0 when the band's top meets the viewport bottom, 1 when its bottom meets the top.
        var rect = root.getBoundingClientRect();
        var vh = window.innerHeight;
        var p = (vh - rect.top) / (vh + rect.height);
        p = Math.min(1, Math.max(0, p));
        root.style.setProperty('--kf-hscroll-x', (-p * maxX).toFixed(1) + 'px');
      }

      function onScroll() {
        if (!frame) frame = window.requestAnimationFrame(paint);
      }

      function disable() {
        root.removeAttribute('data-kf-hscroll');
        root.style.removeProperty('--kf-hscroll-x');
        window.removeEventListener('scroll', onScroll);
      }

      function update() {
        if (!phoneHScroll.matches || reduceMotion.matches) { disable(); return; }
        root.setAttribute('data-kf-hscroll', '');
        // measured with the travel reset, so scrollWidth is the full strip
        root.style.setProperty('--kf-hscroll-x', '0px');
        maxX = Math.max(0, track.scrollWidth - wrapper.clientWidth);
        if (maxX <= 0) { disable(); return; }
        window.removeEventListener('scroll', onScroll);
        window.addEventListener('scroll', onScroll, { passive: true });
        paint();
      }

      var resizeFrame = 0;
      function onResize() {
        if (!resizeFrame) resizeFrame = window.requestAnimationFrame(function () { resizeFrame = 0; update(); });
      }
      window.addEventListener('resize', onResize, { passive: true });
      if (phoneHScroll.addEventListener) phoneHScroll.addEventListener('change', update);
      if (reduceMotion.addEventListener) reduceMotion.addEventListener('change', update);

      root.__kfHScroll = { update: update };
      update();
    });
  }

  function init() {
    try { injectStoryCaptions(); } catch (e) { /* progressive enhancement */ }
    try { initMobileHScroll(); } catch (e) { /* progressive enhancement */ }
    try { initRailCounters(); } catch (e) { /* progressive enhancement */ }
    try { armWarm(); } catch (e) { /* progressive enhancement */ }
    try { initGiveNow(); } catch (e) { /* progressive enhancement */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Avante replaces markup on these documented events — rebind rather than poll.
  document.addEventListener('page:reloaded', init);
  document.addEventListener('filters:rerendered', init);

  // Shopify re-renders a single section when a merchant edits it. Without this the
  // GiveNow frame stays unresized the moment the token is pasted in the editor.
  // Scoped to initGiveNow deliberately: this event fires on EVERY setting change to ANY
  // section in the editor, and the other three behaviours were never written for that
  // cadence. initGiveNow is idempotent — it only ever sees frames still marked "pending".
  document.addEventListener('shopify:section:load', function () {
    try { initGiveNow(); } catch (e) { /* progressive enhancement */ }
  });
})();
